import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorBanner, ListState, formatUtc, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { alerts, type AlertState } from '../../lib/api'
import { CASES_COPY, emptyStateFor } from '../../lib/emptyState'
import { severityIdFromName, severityLook } from '../../lib/severity'

const STATES: { value: string; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'suppressed', label: 'Suppressed' },
  { value: '', label: 'Everything' }
]

/**
 * The queue an analyst works.
 *
 * It opens on the new cases, not on everything: a queue that shows resolved work by default is one
 * where the twelve things needing attention are hidden among four hundred that do not. The occurrence
 * count is on the row for the same reason — one case seen thirty times is a different problem from
 * thirty cases seen once, and the difference decides which one gets picked up first.
 *
 * The severity cell carries its word as well as its color, and that is not decoration. Broadsheet
 * has two inks, so Low and Medium are the same cyan there, and color alone is unreadable to a
 * color-blind operator in every palette (see `lib/severity.ts`).
 */
export function AlertListPage() {
  const [state, setState] = useState<string>('new')
  const [severity, setSeverity] = useState<string>('')
  const list = useAsync(
    () => alerts.list({ state: state || undefined, severity: severity || undefined }),
    [state, severity]
  )

  // "Filtered" means the operator narrowed it. Opening on `new` is this screen's default rather than
  // a filter somebody applied, but an empty `new` queue with a severity chosen is still a filtered
  // nothing - and the fix for that is to clear the severity, not to wait for the fleet.
  const filtered = severity !== '' || (state !== '' && state !== 'new')

  const kind = emptyStateFor({
    value: list.value?.items ?? null,
    error: list.error,
    loading: list.loading,
    filtered,
    // A `new` queue that is empty is genuinely good news, and only when nothing was filtered away.
    // Any other state being empty is an absence this screen cannot interpret, so it says the
    // first-run sentence, which offers a next step rather than congratulating anybody.
    everHadRows: state === 'new' && !filtered
  })

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Cases</PageTitle>
          <p>
            Detections that need a decision. Repeats of the same thing on the same machine within
            fifteen minutes are folded into one case, so a count of thirty is one incident, not thirty.
          </p>
        </div>
      </div>

      <ErrorBanner message={list.error} />

      <div className="card">
        <div className="card-body toolbar">
          {STATES.map((option) => (
            <button
              key={option.value || 'all'}
              type="button"
              className={state === option.value ? 'primary' : undefined}
              onClick={() => setState(option.value)}
              data-testid={`alert-filter-${option.value || 'all'}`}
            >
              {option.label}
            </button>
          ))}
          <select
            aria-label="Severity"
            style={{ width: 'auto', marginLeft: 'auto' }}
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
            data-testid="alert-severity-filter"
          >
            <option value="">Any severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {kind === 'rows' && (
          <div className="table-scroll">
            <table data-testid="alert-table">
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Severity</th>
                  <th>Seen</th>
                  <th className="secondary">State</th>
                  <th className="secondary">Last</th>
                </tr>
              </thead>
              <tbody>
                {list.value?.items.map((alert) => {
                  const look = severityLook(severityIdFromName(alert.severity))

                  return (
                    <tr key={alert.alertId}>
                      <td>
                        <Link to={`/cases/${alert.alertId}`}>{alert.title}</Link>
                        {alert.matchedRuleNames && <div className="muted">{alert.matchedRuleNames}</div>}
                      </td>
                      <td>
                        <span className={`severity ${look.className}`}>
                          <span className="severity-bar" aria-hidden="true" />
                          {look.label}
                        </span>
                      </td>
                      <td data-testid={`alert-occurrences-${alert.alertId}`}>
                        {alert.occurrenceCount === 1 ? 'once' : `${alert.occurrenceCount} times`}
                      </td>
                      <td className="secondary">
                        <span className={`chip chip-${chipFor(alert.state)}`}>{alert.state}</span>
                      </td>
                      <td className="muted secondary">{formatUtc(alert.lastSeenUtc)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <ListState
          kind={kind}
          copy={CASES_COPY}
          columns={5}
          onAction={() => {
            if (kind === 'error') list.reload()
            else {
              setSeverity('')
              setState('new')
            }
          }}
        />
      </div>
    </>
  )
}

/** Which tint pair a case state takes. New is the one that needs somebody; resolved is settled. */
function chipFor(state: string): 'new' | 'ack' | 'done' {
  if (state === 'new') return 'new'
  if (state === 'resolved' || state === 'suppressed') return 'done'
  return 'ack'
}

export const ALERT_STATES: AlertState[] = ['new', 'acknowledged', 'assigned', 'resolved', 'suppressed']
